import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authService = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  validateToken: () => api.get('/auth/validate'),
};

// Category APIs
export const categoryService = {
  getAll: (params) => api.get('/categories', { params }),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  updateStatus: (id, status) => api.patch(`/categories/${id}/status`, { status }),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Team APIs
export const teamService = {
  getAll: (params) => api.get('/teams', { params }),
  getById: (id) => api.get(`/teams/${id}`),
  getTeamsByCategories: (categoryCodes) => api.get('/teams/by-categories', { params: { categoryCodes: Array.isArray(categoryCodes) ? categoryCodes.join(',') : categoryCodes } }),
  getTeamsByRegionAndCategory: (regionCodes, categoryCodes) => api.get('/teams/by-region-and-category', { params: { regionCodes: Array.isArray(regionCodes) ? regionCodes.join(',') : regionCodes, categoryCodes: Array.isArray(categoryCodes) ? categoryCodes.join(',') : categoryCodes } }),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  updateStatus: (id, status) => api.patch(`/teams/${id}/status`, { status }),
  delete: (id) => api.delete(`/teams/${id}`),
  searchRegions: (searchTerm, includeCodes) => api.get('/teams/dropdown/regions', { params: { search: searchTerm, include_codes: includeCodes } }),
  searchCategories: (searchTerm, categoryType, includeCodes) => api.get('/teams/dropdown/categories', { params: { search: searchTerm, category_type: categoryType, include_codes: includeCodes } }),
  searchBranches: (searchTerm, regionCodes, includeCodes) => api.get('/teams/dropdown/branches', { params: { search: searchTerm, region_codes: regionCodes, include_codes: includeCodes } }),
  getUsersForDropdown: (regionCodes, selectedUserIds, searchTerm) => api.post('/teams/dropdown/users', { regionCodes, selectedUserIds, search: searchTerm }),
};

// Status APIs
export const statusService = {
  getAll: (params) => api.get('/statuses', { params }),
  create: (data) => api.post('/statuses', data),
  update: (id, data) => api.put(`/statuses/${id}`, data),
  updateStatus: (id, status) => api.patch(`/statuses/${id}/status`, { status }),
  delete: (id) => api.delete(`/statuses/${id}`),
};

// Tag APIs
export const tagService = {
  getAll: (params) => api.get('/tags', { params }),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  updateStatus: (id, status) => api.patch(`/tags/${id}/status`, { status }),
  delete: (id) => api.delete(`/tags/${id}`),
};

// Ticket APIs
export const ticketService = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  getMyTickets: (params) => api.get('/tickets/my-tickets', { params }),
  getDashboardStats: () => api.get('/tickets/dashboard-stats'),
  create: (data) => {
    // If data contains files, send as FormData
    if (data.attachments && data.attachments.length > 0) {
      const formData = new FormData();
      
      // Append all non-file fields
      Object.keys(data).forEach(key => {
        if (key !== 'attachments') {
          if (Array.isArray(data[key])) {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });
      
      // Append files
      data.attachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      return api.post('/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/tickets', data);
  },
  update: (id, data) => api.put(`/tickets/${id}`, data),
  updateStatus: (id, status, remarks) => api.patch(`/tickets/${id}/status`, { status, remarks }),
  assignTicket: (id, user_id, remarks) => api.patch(`/tickets/${id}/assign`, { user_id, remarks }),
  getEligibleUsersForAssignment: (ticketId) => api.get(`/tickets/${ticketId}/eligible-users`),
  uploadAttachment: (ticketId, formData) => api.post(`/tickets/${ticketId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  reopenTicket: (id, reason) => api.patch(`/tickets/${id}/reopen`, { reason }),
  delete: (id) => api.delete(`/tickets/${id}`),
  addRemark: (ticketId, remark) => api.post(`/tickets/${ticketId}/remarks`, { remark }),
  submitAction: (ticketId, formData) => api.post(`/tickets/${ticketId}/action`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getHistory: (id) => api.get(`/tickets/${id}/history`),
  markAsRead: (id) => api.post(`/tickets/${id}/mark-read`),
  getSummaryCards: () => api.get('/tickets/summary-cards'),
  // Coordinator queue: tickets with nobody assigned yet. Returns 0 for roles
  // that cannot assign, so callers do not need their own permission check.
  getAwaitingAssignmentCount: () => api.get('/tickets/awaiting-assignment/count'),
  getEscalationInfo: (id) => api.get(`/tickets/${id}/escalation-info`),
  escalateTicket: (id, data) => api.post(`/tickets/${id}/escalate`, data),
  deEscalateTicket: (id, data) => api.post(`/tickets/${id}/de-escalate`, data),
};

export const commentService = {
  getByTicket: (ticketId) => api.get(`/tickets/${ticketId}/comments`),
  create: (ticketId, data) => api.post(`/tickets/${ticketId}/comments`, data),
  update: (commentId, data) => api.put(`/tickets/comments/${commentId}`, data),
  delete: (commentId) => api.delete(`/tickets/comments/${commentId}`),
};

// Region APIs
export const regionService = {
  getAll: (params) => api.get('/regions', { params }),
  getById: (id) => api.get(`/regions/${id}`),
  create: (data) => api.post('/regions', data),
  update: (id, data) => api.put(`/regions/${id}`, data),
  updateStatus: (id, status) => api.patch(`/regions/${id}/status`, { status }),
  delete: (id) => api.delete(`/regions/${id}`),
};

// Branch APIs
export const branchService = {
  getAll: (params) => api.get('/branches', { params }),
  getById: (id) => api.get(`/branches/${id}`),
  getUnmapped: () => api.get('/branches/unmapped'),
  getByRegion: (regionCode) => api.get(`/branches/region/${regionCode}`),
  getByRegions: (regionCodes) => api.get('/branches', { params: { region_codes: Array.isArray(regionCodes) ? regionCodes.join(',') : regionCodes, noPagination: 'true' } }),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  updateStatus: (id, status) => api.patch(`/branches/${id}/status`, { status }),
  delete: (id) => api.delete(`/branches/${id}`),
};

// Sender Email APIs
export const senderEmailService = {
  getAll: (params) => api.get('/sender-emails', { params }),
  getById: (id) => api.get(`/sender-emails/${id}`),
  create: (data) => api.post('/sender-emails', data),
  update: (id, data) => api.put(`/sender-emails/${id}`, data),
  updateStatus: (id, status) => api.patch(`/sender-emails/${id}/status`, { status }),
  delete: (id) => api.delete(`/sender-emails/${id}`),
};

// Equipment APIs — BioMedical only. IT's equivalent master is Tags.
export const equipmentService = {
  getAll: (params) => api.get('/equipment', { params }),
  getById: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  updateStatus: (id, status) => api.patch(`/equipment/${id}/status`, { status }),
  delete: (id) => api.delete(`/equipment/${id}`),
  upload: (formData) => api.post('/equipment/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Files many records under one category: either the ticked ids, or
  // everything matching the filters the screen is showing (all: true).
  bulkCategory: (body, params) => api.patch('/equipment/bulk-category', body, { params }),
  // The filtered list as CSV, in the upload file's columns
  exportCsv: (params) => api.get('/equipment/export', { params, responseType: 'blob' }),
  getHistory: (id) => api.get(`/master-history/equipment/${id}`),
};

// User APIs
export const userService = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getByTeam: (teamCode) => api.get(`/users/team/${teamCode}`),
  getByTeams: (teamCodes) => api.get('/users/teams', { params: { teamCodes: teamCodes.join(',') } }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateStatus: (id, status) => api.patch(`/users/${id}/status`, { status }),
  delete: (id) => api.delete(`/users/${id}`),
};

export const roleService = {
  getAll: (params) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  updateStatus: (id, status) => api.patch(`/roles/${id}/status`, { status }),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Dashboard Analytics APIs
export const dashboardService = {
  getSummary: (params) => api.get('/dashboard/summary', { params }),
  getTicketVolume: (params) => api.get('/dashboard/ticket-volume', { params }),
  getStatusSnapshot: (params) => api.get('/dashboard/status-snapshot', { params }),
  getResolutionTime: (params) => api.get('/dashboard/resolution-time', { params }),
  getCategoryBreakdown: (params) => api.get('/dashboard/category-breakdown', { params }),
  getRegionalPerformance: (params) => api.get('/dashboard/regional-performance', { params }),
  getTeamPerformance: (params) => api.get('/dashboard/team-performance', { params }),
  getItTeamPerformance: (params) => api.get('/dashboard/it-team-performance', { params }),
  getFilters: (params) => api.get('/dashboard/filters', { params }),
  getCategoryBreakdownAll: (params) => api.get('/dashboard/category-breakdown', { params: { ...params, limit: 'all' } }),
  getRegionalPerformanceAll: (params) => api.get('/dashboard/regional-performance', { params: { ...params, limit: 'all' } }),
  getTeamPerformanceAll: (params) => api.get('/dashboard/team-performance', { params: { ...params, limit: 'all' } }),
};

// Advanced Reports APIs
export const advancedReportService = {
  getFilters: () => api.get('/advanced-reports/filters'),
  getCategoryReport: (params) => api.get('/advanced-reports/category', { params }),
  getRegionReport: (params) => api.get('/advanced-reports/region', { params }),
  getSlaReport: (params) => api.get('/advanced-reports/sla', { params }),
  getItTeamReport: (params) => api.get('/advanced-reports/it-team', { params }),
  getItTeamTickets: (params) => api.get('/advanced-reports/it-team/tickets', { params }),
  // BioMedical only — tickets per device, so a machine that keeps failing is
  // visible rather than buried in the category totals.
  getEquipmentReport: (params) => api.get('/advanced-reports/equipment', { params }),
};

// Escalation Rule APIs
export const escalationService = {
  getAll:        (params) => api.get('/escalations', { params }),
  getById:       (id)     => api.get(`/escalations/${id}`),
  create:        (data)   => api.post('/escalations', data),
  update:        (id, data) => api.put(`/escalations/${id}`, data),
  updateStatus:  (id, status) => api.patch(`/escalations/${id}/status`, { status }),
  delete:        (id)     => api.delete(`/escalations/${id}`),
  getRegionsDropdown:    (includeIds) => api.get('/escalations/dropdown/regions', { params: { include_ids: includeIds } }),
  getCategoriesDropdown: (includeIds) => api.get('/escalations/dropdown/categories', { params: { include_ids: includeIds } }),
  getUsersDropdown:      (includeIds) => api.get('/escalations/dropdown/users', { params: { include_ids: includeIds } }),
  // BioMedical only. categoryIds narrows the list to devices mapped to those
  // categories; includeIds keeps ones the rule already holds selectable.
  getEquipmentDropdown:  (categoryIds, includeIds) => api.get('/escalations/dropdown/equipment', {
    params: { category_ids: categoryIds, include_ids: includeIds }
  }),
  validateCombo:         (data) => api.post('/escalations/validate-combo', data),
};

// SLA Rules & Rating Thresholds APIs
export const slaService = {
  getAll: () => api.get('/sla-rules'),
  getById: (id) => api.get(`/sla-rules/${id}`),
  create: (data) => api.post('/sla-rules', data),
  update: (id, data) => api.put(`/sla-rules/${id}`, data),
  delete: (id) => api.delete(`/sla-rules/${id}`),
  getRatingThresholds: () => api.get('/sla-rules/rating-thresholds'),
  updateRatingThreshold: (id, data) => api.put(`/sla-rules/rating-thresholds/${id}`, data),
};

export default api;
